import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { getCurrentUser, fetchAuthSession, signIn, signOut, confirmSignIn } from "aws-amplify/auth";
import { apiMfaSignIn, apiMfaSignInConfirm, apiMfaSetup, apiMfaVerify, apiNewPassword, apiWebSignIn, clearSessionTokens, recordLogout, refreshSession, TOKENS_REFRESHED_EVENT } from "../lib/api";
import { startActivityTracking, markActivity, resetActivity, clearActivity, isIdleExpired, IDLE_CHECK_MS } from "../lib/idleSession";

const AuthContext = createContext(null);

// Map Cognito groups to application roles
function resolveRole(groups = []) {
  if (groups.includes("FleetAdmin")) return "FleetAdmin";
  if (groups.includes("ReleaseManager")) return "ReleaseManager";
  if (groups.includes("SupportEngineer")) return "SupportEngineer";
  // Read-only roles (003 Role Gate): can view, but never receive download URLs.
  if (groups.includes("Nurse")) return "Nurse";
  if (groups.includes("Patient")) return "Patient";
  return null;
}

// ─── DEV OVERRIDE ─────────────────────────────────────────────────────────────
// When REACT_APP_USE_MOCK_AUTH=true the app bypasses Cognito so you can develop
// the UI without a deployed backend. Set this to "false" once AWS is wired.
const USE_MOCK_AUTH = process.env.REACT_APP_USE_MOCK_AUTH === "true";

// ── Switch role here to test different access levels ──────────────────────────
// Options: "FleetAdmin" | "ReleaseManager" | "SupportEngineer" | "Nurse" | "Patient"
const ACTIVE_ROLE = "FleetAdmin"; // "FleetAdmin" | "ReleaseManager" | "SupportEngineer" | "Nurse" | "Patient"

const MOCK_USER = {
  sub: "sub-001",
  email: "admin@tetra.med",
  name: "Alice Admin",
  role: ACTIVE_ROLE,
  groups: [ACTIVE_ROLE],
};
// ──────────────────────────────────────────────────────────────────────────────

const SESSION_EXPIRED_KEY = "session_expired";
// Which sign-in tab issued the current session ("mfa" or "web"), so step-up
// re-authentication asks for the same credentials the user signed in with.
const SIGNIN_METHOD_KEY = "signin_method";
// "idle" or "expired" — what the login page tells the user.
const SESSION_END_REASON_KEY = "session_end_reason";

function setSessionExpiredMarker(reason = "expired") {
  sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  sessionStorage.setItem(SESSION_END_REASON_KEY, reason);
  localStorage.removeItem(SESSION_EXPIRED_KEY);
}

function clearSessionExpiredMarker() {
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  sessionStorage.removeItem(SESSION_END_REASON_KEY);
  localStorage.removeItem(SESSION_EXPIRED_KEY);
}

// Decode JWT payload without verification (trust is established by the API call itself).
// Returns {} for anything unparseable, so callers can probe fields without a try/catch.
function decodeJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Holds the server-side session token between MFA challenge steps.
  const mfaSessionRef = useRef(null);
  // Holds the access_token returned by /auth/new-password (used by setup + verify).
  const accessTokenRef = useRef(null);
  // Holds the email used to initiate sign-in (needed for /auth/new-password).
  const emailRef = useRef(null);
  // Holds the tokens returned by /auth/mfa/signin (used by /auth/mfa/signin/confirm).
  const mfaSigninTokensRef = useRef({ access_token: null, id_token: null, refresh_token: null });
  // Holds { email, session } between the two steps of a step-up re-authentication.
  // Separate from the sign-in refs so a re-auth can never disturb a login flow.
  const reauthPendingRef = useRef(null);

  // Persist tokens from the MFA API response and hydrate user state.
  const setSessionFromTokens = useCallback((tokens) => {
    const idToken =
      tokens?.IdToken || tokens?.idToken || tokens?.id_token ||
      tokens?.AuthenticationResult?.IdToken || null;
    if (!idToken) return;
    localStorage.setItem("mfa_id_token", idToken);
    const accessToken =
      tokens?.AccessToken || tokens?.accessToken || tokens?.access_token ||
      tokens?.AuthenticationResult?.AccessToken || null;
    if (accessToken) localStorage.setItem("mfa_access_token", accessToken);
    // Persisting this is what makes silent refresh possible at all. Without it the
    // refresh token lives only in a React ref and is lost on reload, so the session
    // dies when the 60-minute id token expires despite the refresh token having days left.
    const refreshToken =
      tokens?.RefreshToken || tokens?.refreshToken || tokens?.refresh_token ||
      tokens?.AuthenticationResult?.RefreshToken || null;
    if (refreshToken) localStorage.setItem("mfa_refresh_token", refreshToken);
    // FMP-AU-005: a new session starts the idle clock, so a stale timestamp from
    // the previous one cannot expire this one the moment it begins.
    resetActivity();
    const payload = decodeJwtPayload(idToken);
    const groups = payload["cognito:groups"] || [];
    setUser({
      sub: payload.sub,
      email: payload.email || payload["cognito:username"] || "",
      name: payload.name || payload.email || "",
      groups,
    });
    setRole(resolveRole(groups));
  }, []);

  const loadSession = useCallback(async () => {
    if (USE_MOCK_AUTH) {
      setUser(MOCK_USER);
      setRole(MOCK_USER.role);
      setLoading(false);
      return { user: MOCK_USER, role: MOCK_USER.role };
    }

    // Check for a token issued by the new MFA backend API.
    const mfaToken = localStorage.getItem("mfa_id_token");
    if (mfaToken) {
      // Returns the resolved identity so every exit path of loadSession reports
      // one, which is what callers like the SSO callback rely on.
      const hydrate = (token) => {
        const payload = decodeJwtPayload(token);
        // An unreadable token must not produce a half-built, role-less session.
        // Throwing here is what routes the refresh path below to session-end.
        if (!payload.sub) throw new Error("Unreadable id token");
        const groups = payload["cognito:groups"] || [];
        const nextUser = {
          sub: payload.sub,
          email: payload.email || payload["cognito:username"] || "",
          name: payload.name || payload.email || "",
          groups,
        };
        const nextRole = resolveRole(groups);
        setUser(nextUser);
        setRole(nextRole);
        setLoading(false);
        return { user: nextUser, role: nextRole };
      };

      // A corrupt token decodes to {}, so it has no `exp` and falls through the
      // same way an expired one does.
      const mfaPayload = decodeJwtPayload(mfaToken);
      if (mfaPayload.exp && mfaPayload.exp * 1000 > Date.now()) {
        return hydrate(mfaToken);
      }

      // Id token expired. Before ending the session, try the refresh token — this
      // is the reload-after-an-hour case that used to bounce the user to /login
      // even though their refresh token was still valid for days.
      //
      // FMP-AU-005: unless the session went idle first, in which case it is over
      // and the refresh token must not revive it. Checked before the attempt so a
      // reload after a long absence lands on /login rather than silently resuming.
      const endedIdle = isIdleExpired();
      if (!endedIdle && localStorage.getItem("mfa_refresh_token")) {
        try {
          // refreshSession stores the new tokens itself and returns the id token.
          const refreshedIdToken = await refreshSession();
          if (refreshedIdToken) {
            return hydrate(refreshedIdToken);
          }
        } catch {
          // Transient refresh failure — fall through and end the session. The user
          // can sign in again; silently retrying here would stall the app on load.
        }
      }

      // Idle, or the refresh token is absent, rejected or unusable — the session
      // really is over. An idle session's refresh token is still valid for days,
      // so it is revoked server-side before it is dropped (TS-07-TC-0003 FT-002,
      // DISC-004); a dead one makes this a no-op.
      recordLogout(endedIdle ? "idle" : "expired");
      localStorage.removeItem("mfa_id_token");
      localStorage.removeItem("mfa_access_token");
      localStorage.removeItem("mfa_refresh_token");
      clearActivity();
      setSessionExpiredMarker(endedIdle ? "idle" : "expired");
      setUser(null);
      setRole(null);
      setSessionExpired(true);
      setLoading(false);
      return { user: null, role: null };
    }

    // Amplify-owned session. This is also the branch federated (Hosted UI /
    // SAML) sign-ins land in: they never write the mfa_* keys above, so SSO
    // needs no third code path here.
    try {
      const cognitoUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups =
        session.tokens?.idToken?.payload["cognito:groups"] || [];
      const email =
        session.tokens?.idToken?.payload["email"] || cognitoUser.username;
      const name =
        session.tokens?.idToken?.payload["name"] || email;
      const nextUser = { sub: cognitoUser.userId, email, name, groups };
      const nextRole = resolveRole(groups);
      setUser(nextUser);
      setRole(nextRole);
      clearSessionExpiredMarker();
      // Returned so the SSO callback can act on the resolved role immediately
      // instead of racing React state to decide whether to admit the user.
      return { user: nextUser, role: nextRole };
    } catch {
      setUser(null);
      setRole(null);
      return { user: null, role: null };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // A silent token refresh can return different cognito:groups than the token it
  // replaced, because an admin changed this user's role while the tab was open.
  // Re-deriving the role here is what makes a demotion take effect without a
  // reload; ProtectedRoute already renders its Access Denied panel when the new
  // role no longer covers the current page.
  useEffect(() => {
    if (USE_MOCK_AUTH) return;   // never clobber the hardcoded dev role

    const onTokensRefreshed = (event) => {
      const idToken = event?.detail?.idToken;
      if (!idToken) return;
      const payload = decodeJwtPayload(idToken);
      // Same guard as hydrate(): an unreadable token must not produce a
      // half-built, role-less session out of a session that was working.
      if (!payload.sub) return;
      const groups = payload["cognito:groups"] || [];
      setUser({
        sub: payload.sub,
        email: payload.email || payload["cognito:username"] || "",
        name: payload.name || payload.email || "",
        groups,
      });
      setRole(resolveRole(groups));
    };

    window.addEventListener(TOKENS_REFRESHED_EVENT, onTokensRefreshed);
    return () => window.removeEventListener(TOKENS_REFRESHED_EVENT, onTokensRefreshed);
  }, []);

  // FMP-AU-005: end an idle session without waiting for the user to come back.
  //
  // Only runs while someone is signed in. The sweep matters as much as the
  // refresh gate in api.js: without it an unattended screen keeps showing fleet
  // data until somebody touches it, which is the thing an idle timeout exists to
  // prevent. Clearing `user` is what sends ProtectedRoute to /login.
  useEffect(() => {
    if (USE_MOCK_AUTH || !user) return undefined;

    // Seed the clock for a session restored from storage, which never passed
    // through setSessionFromTokens and so has no timestamp of its own.
    markActivity();
    const stopTracking = startActivityTracking();

    const sweep = setInterval(() => {
      if (!isIdleExpired()) return;
      // Revoke server-side before letting go of the tokens (TS-07-TC-0003 FT-002,
      // DISC-004): reads them synchronously, so clearing straight after is safe.
      recordLogout("idle");
      clearSessionTokens();
      clearActivity();
      setSessionExpiredMarker("idle");
      setUser(null);
      setRole(null);
      setSessionExpired(true);
    }, IDLE_CHECK_MS);

    return () => {
      stopTracking();
      clearInterval(sweep);
    };
  }, [user]);

  const login = useCallback(async ({ email, password }) => {
    console.log('[Auth] login called, USE_MOCK_AUTH=', USE_MOCK_AUTH);
    if (USE_MOCK_AUTH) {
      setUser(MOCK_USER);
      setRole(MOCK_USER.role);
      return { isSignedIn: true };
    }
    console.log('[Auth] calling signIn for:', email);
    let result;
    try {
      result = await signIn({ username: email, password });
    } catch (err) {
      console.error('[Auth] signIn threw:', err);
      throw err;
    }
    console.log('[Auth] signIn result:', result);
    if (result.isSignedIn) {
      await loadSession();
    }
    // nextStep values handled by LoginPage:
    // - CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED  → set permanent password
    // - CONTINUE_SIGN_IN_WITH_TOTP_SETUP            → scan QR code in authenticator app
    // - CONFIRM_SIGN_IN_WITH_TOTP_CODE              → enter OTP from authenticator app
    // - CONFIRM_SIGN_IN_WITH_SMS_MFA                → enter SMS OTP
    return result;
  }, [loadSession]);

  // Called after NEW_PASSWORD_REQUIRED challenge.
  // Uses the new MFA API when a session is available, otherwise falls back to Cognito.
  const confirmNewPassword = useCallback(async (newPassword) => {
    if (mfaSessionRef.current) {
      const result = await apiMfaSignInConfirm(mfaSessionRef.current, newPassword);
      const tokens = result?.AuthenticationResult || result;
      const idToken = tokens?.IdToken || tokens?.idToken || tokens?.id_token || null;
      if (idToken) {
        setSessionFromTokens(tokens);
        return { isSignedIn: true };
      }
      // Challenge continues (e.g. TOTP still required after password change).
      mfaSessionRef.current = result?.Session || result?.session || mfaSessionRef.current;
      return result;
    }
    // Cognito fallback (Standard Sign In path).
    const result = await confirmSignIn({ challengeResponse: newPassword });
    if (result.isSignedIn) {
      await loadSession();
    }
    return result;
  }, [loadSession, setSessionFromTokens]);

  // Called after SMS_MFA or TOTP_CODE challenge (Cognito path – Standard Sign In)
  const confirmMfa = useCallback(async (otpCode) => {
    const result = await confirmSignIn({ challengeResponse: otpCode });
    if (result.isSignedIn) {
      await loadSession();
    }
    return result;
  }, [loadSession]);

  // ── New MFA backend API functions (MFA Sign In tab) ───────────────────────

  /**
   * Step 1 – Initiate sign-in via the new MFA backend.
   * Stores the server session for use in subsequent challenge steps.
   */
  const mfaLogin = useCallback(async (email, password) => {
    if (USE_MOCK_AUTH) {
      setUser(MOCK_USER);
      setRole(MOCK_USER.role);
      return { isSignedIn: true };
    }
    const result = await apiMfaSignIn(email, password);
    console.log('[Auth] mfaLogin raw result:', result);
    localStorage.setItem(SIGNIN_METHOD_KEY, "mfa");
    // Store email and session for the new-password challenge path.
    emailRef.current = email;
    mfaSessionRef.current =
      result?.Session || result?.session ||
      result?.sessionToken || result?.SessionToken || null;
    // Store tokens for the MFA confirm path (/auth/mfa/signin/confirm).
    mfaSigninTokensRef.current = {
      access_token:  result?.access_token  || result?.AccessToken  || null,
      id_token:      result?.id_token      || result?.IdToken      || null,
      refresh_token: result?.refresh_token || result?.RefreshToken || null,
    };
    // Return isSignedIn only when actual tokens are present.
    const idToken =
      result?.AuthenticationResult?.IdToken ||
      result?.idToken || result?.id_token || result?.token || null;
    if (idToken) {
      setSessionFromTokens(result?.AuthenticationResult || result);
      return { isSignedIn: true };
    }
    return result;
  }, [setSessionFromTokens]);

  /**
   * Step 2a – Confirm TOTP/SMS OTP code.
   * Calls /auth/mfa/signin/confirm with the stored session.
   */
  const confirmMfaSignIn = useCallback(async (code) => {
    const result = await apiMfaSignInConfirm(emailRef.current, mfaSessionRef.current, code);
    setSessionFromTokens(result?.AuthenticationResult || result);
    return result;
  }, [setSessionFromTokens]);

  /**
   * Step 2b – Fetch QR code + secret for first-time TOTP setup.
   * Uses the access_token stored by setNewPasswordApi.
   */
  const fetchMfaSetup = useCallback(async () => {
    const result = await apiMfaSetup(accessTokenRef.current);
    return result;
  }, []);

  /**
   * Step 2b helper – Set new password after NEW_PASSWORD_REQUIRED challenge.
   * Calls POST /auth/new-password; stores access_token and id_token for MFA steps.
   */
  const setNewPasswordApi = useCallback(async (newPassword) => {
    const result = await apiNewPassword(
      emailRef.current,
      newPassword,
      mfaSessionRef.current
    );
    console.log('[Auth] setNewPassword result:', result);
    // Store access_token — required as Bearer token for /auth/mfa/setup and /auth/mfa/verify.
    accessTokenRef.current =
      result?.access_token || result?.AccessToken || result?.accessToken || null;
    // Store id_token now; it will be activated into user state after MFA verify succeeds.
    const idToken = result?.id_token || result?.IdToken || result?.idToken || null;
    if (idToken) localStorage.setItem("mfa_pending_id_token", idToken);
    return result;
  }, []);

  /**
   * Step 4 – Verify the TOTP code to complete MFA enrollment.
   * Uses the access_token returned by /auth/new-password.
   */
  const verifyMfaSetup = useCallback(async (otpCode) => {
    const result = await apiMfaVerify(accessTokenRef.current, otpCode);
    // Activate session: use the id_token stored during setNewPasswordApi.
    const pendingToken = localStorage.getItem("mfa_pending_id_token");
    if (pendingToken) {
      localStorage.setItem("mfa_id_token", pendingToken);
      localStorage.removeItem("mfa_pending_id_token");
      setSessionFromTokens({ idToken: pendingToken });
    }
    return result;
  }, [setSessionFromTokens]);

  // Standard (non-MFA) web sign-in via POST /web-signin.
  const webSignIn = useCallback(async (email, password) => {
    if (USE_MOCK_AUTH) {
      setUser(MOCK_USER);
      setRole(MOCK_USER.role);
      return { isSignedIn: true };
    }
    const result = await apiWebSignIn(email, password);
    console.log('[Auth] webSignIn result:', result);
    localStorage.setItem(SIGNIN_METHOD_KEY, "web");
    const idToken =
      result?.id_token || result?.IdToken || result?.idToken ||
      result?.AuthenticationResult?.IdToken || null;
    if (idToken) {
      setSessionFromTokens(result?.AuthenticationResult || result);
      return { isSignedIn: true };
    }
    return result;
  }, [setSessionFromTokens]);

  // ── Step-up re-authentication (TP-SAF-WEB-TS-07-TC-0003 FT-004) ───────────
  // A safety-critical action (publish) is refused by the service once the
  // session's sign-in is older than the re-auth window. These re-enter the
  // credentials through the same sign-in endpoint the session came from - so MFA,
  // lockout and the USER_LOGIN audit all apply - and swap in the fresh tokens.
  // The user stays on the page; nothing about the current session changes unless
  // the re-authentication succeeds for the same account.

  const signInRequiredError = (message) => {
    const err = new Error(message || "This session cannot be re-confirmed here. Sign out and sign in again, then retry.");
    err.signInRequired = true;
    return err;
  };

  const adoptReauthTokens = useCallback((result) => {
    const tokens = result?.AuthenticationResult || result;
    const idToken = tokens?.IdToken || tokens?.idToken || tokens?.id_token || null;
    if (!idToken) throw new Error("Re-authentication did not complete. Please try again.");
    // Never let a re-authentication switch the signed-in account.
    if (!user?.sub || decodeJwtPayload(idToken).sub !== user.sub) {
      throw new Error("Re-authenticate with the account that is signed in.");
    }
    setSessionFromTokens(tokens);
    return { reauthenticated: true };
  }, [user, setSessionFromTokens]);

  /**
   * Step 1 – re-enter the password. Resolves { reauthenticated: true } when that
   * is enough, or { codeRequired: true } when an MFA code must follow.
   */
  const reauthenticate = useCallback(async (password) => {
    reauthPendingRef.current = null;
    if (USE_MOCK_AUTH) return { reauthenticated: true };
    const email = user?.email;
    // SSO / Amplify sessions have no password here; they sign in again instead.
    if (!email || !localStorage.getItem("mfa_id_token")) throw signInRequiredError();

    if (localStorage.getItem(SIGNIN_METHOD_KEY) === "web") {
      return adoptReauthTokens(await apiWebSignIn(email, password));
    }

    const result = await apiMfaSignIn(email, password);
    const challenge = String(result?.challenge || result?.ChallengeName || "").toUpperCase();
    if (!challenge) return adoptReauthTokens(result);
    const session = result?.session || result?.Session || null;
    if ((challenge.includes("SOFTWARE_TOKEN") || challenge.includes("SMS_MFA")) && session) {
      reauthPendingRef.current = { email, session };
      return { codeRequired: true };
    }
    // New-password or MFA-setup challenges belong on the sign-in page.
    throw signInRequiredError();
  }, [user, adoptReauthTokens]);

  /** Step 2 – the MFA code, when reauthenticate asked for one. */
  const confirmReauthentication = useCallback(async (code) => {
    const pending = reauthPendingRef.current;
    if (!pending) throw new Error("Enter your password first.");
    const result = await apiMfaSignInConfirm(pending.email, pending.session, code);
    reauthPendingRef.current = null;
    return adoptReauthTokens(result);
  }, [adoptReauthTokens]);

  const logout = useCallback(async (reason) => {
    if (USE_MOCK_AUTH) {
      setUser(null);
      setRole(null);
      if (reason === "expired") setSessionExpired(true);
      return;
    }
    // Record the sign-out on the server first, while the token is still here
    // (FMP-AT-002). Bounded and error-proof - it cannot hold the sign-out up.
    await recordLogout(reason === "expired" || reason === "idle" ? reason : "user");
    // Clear MFA API tokens. The refresh token must go too — leaving it would let
    // the next page load silently resurrect a session the user deliberately ended.
    localStorage.removeItem("mfa_id_token");
    localStorage.removeItem("mfa_access_token");
    localStorage.removeItem("mfa_refresh_token");
    localStorage.removeItem("mfa_pending_id_token");
    localStorage.removeItem(SIGNIN_METHOD_KEY);
    reauthPendingRef.current = null;
    // The idle clock belongs to the session that just ended (FMP-AU-005).
    clearActivity();
    mfaSessionRef.current = null;
    accessTokenRef.current = null;
    emailRef.current = null;
    mfaSigninTokensRef.current = { access_token: null, id_token: null, refresh_token: null };
    // Sign out of Cognito only when there is actually an Amplify session to end.
    // With the Hosted UI configured, signOut() performs a *redirect* sign-out via
    // the Cognito /logout endpoint. That is correct for Amplify and federated
    // sessions — it clears the Cognito cookie, so the next visit cannot silently
    // re-authenticate — but an MFA-API session has no Amplify session to end, and
    // bouncing it through Cognito would add a pointless redirect to every logout.
    let hasAmplifySession = false;
    try {
      hasAmplifySession = Boolean((await fetchAuthSession()).tokens?.idToken);
    } catch {
      hasAmplifySession = false;
    }
    if (hasAmplifySession) {
      try { await signOut(); } catch { /* ignore Cognito errors when not using Cognito */ }
    }
    setUser(null);
    setRole(null);
    if (reason === "expired" || reason === "idle") {
      setSessionExpiredMarker(reason);
      setSessionExpired(true);
    } else {
      clearSessionExpiredMarker();
    }
  }, []);

  const canAccess = useCallback(
    (...allowedRoles) => allowedRoles.includes(role),
    [role]
  );

  const clearSessionExpired = useCallback(() => {
    clearSessionExpiredMarker();
    setSessionExpired(false);
  }, []);

  const value = useMemo(
    () => ({
      user, role, loading, sessionExpired, clearSessionExpired,
      login, logout, confirmNewPassword, confirmMfa, mfaLogin,
      setNewPasswordApi, confirmMfaSignIn, fetchMfaSetup, verifyMfaSetup,
      webSignIn, canAccess, refreshAuthState: loadSession,
      reauthenticate, confirmReauthentication,
    }),
    [
      user, role, loading, sessionExpired, clearSessionExpired,
      login, logout, confirmNewPassword, confirmMfa, mfaLogin,
      setNewPasswordApi, confirmMfaSignIn, fetchMfaSetup, verifyMfaSetup,
      webSignIn, canAccess, loadSession,
      reauthenticate, confirmReauthentication,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
